import { useState } from "react";
import { FiArrowUpRight, FiArrowRight } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import "../styles/concierge.css";
import { useAuth } from "../context/AuthContext";
import { conciergeApi, externalLinkProps, withAwinClickRef } from "../services/externalPlatformApi";

const SERVICES = [
  "Tickets",
  "Hotels",
  "Villas",
  "Flights",
  "Transfers",
  "VIP Tables",
  "After Parties",
  "Villa Parties",
  "Yacht Days",
  "Beach Clubs",
  "Birthday Trips",
  "Full Package",
];

const HOTEL_PICKS = [
  {
    name: "THB Naeco Ibiza - Adults Only",
    meta: "4-star hotel | 9.0 Excellent | San Antonio de Portmany",
    detail: "Adults-only poolside base with clean white terraces, strong reviews and easy access to Ibiza summer nights.",
    image:
      "/assets/hotels/thb-naeco-ibiza.jpg",
    url: "https://tidd.ly/4wjWdRt",
  },
  {
    name: "Hotel Vibra Mare Nostrum",
    meta: "3-star hotel | 8.6 Very Good | Playa d'en Bossa",
    detail: "A practical Playa d'en Bossa stay close to the island's main party route, pools and beach energy.",
    image: "/assets/community/zzz2333333.avif?v=20260817-3",
    url: "https://tidd.ly/49c5amb",
  },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  destination: "",
  party_size: "2",
  dates: "",
  budget: "",
  special_requests: "",
  services: [],
};

export default function ConciergePage() {
  const { user } = useAuth();
  const affiliateCode = user?.affiliateCode || user?.affiliate_code || user?.code;
  const [form, setForm] = useState(EMPTY_FORM);
  const [referral, setReferral] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function toggleService(service) {
    setForm((current) => ({
      ...current,
      services: current.services.includes(service)
        ? current.services.filter((item) => item !== service)
        : [...current.services, service],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (saving) return;
    const destination = form.destination.trim();
    if (!destination) {
      setStatus("Please enter your destination or event.");
      return;
    }
    setSaving(true);
    setStatus("");
    try {
      await conciergeApi.submit({
        ...form,
        destination,
        // Keep referrals in the existing stored details and admin notification email.
        special_requests: [referral.trim() ? `Referral: ${referral.trim()}` : "", form.special_requests.trim()]
          .filter(Boolean).join("\n\n"),
        party_size: Number(form.party_size || 1),
      });
      setForm(EMPTY_FORM);
      setReferral("");
      setStatus("Request received. The concierge team will review this and respond with next steps.");
    } catch {
      setStatus("Your enquiry could not be sent. Please try again or email support@hey-lets-travel.com.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="concierge-page">
      <header className="concierge-heading">
        <p className="concierge-eyebrow">HLT / Private concierge</p>
        <h1>Your plans. Personally arranged.</h1>
        <p>Stays, access and every detail in between. Tell us what you have in mind.</p>
        <div className="concierge-direct-contact">
          <p>Or speak directly to a member of our team.</p>
          <a href="https://wa.me/message/5UT3QJZMUBKNP1" target="_blank" rel="noopener noreferrer">
            <FaWhatsapp aria-hidden="true" /> <span>Chat with us on WhatsApp</span>
          </a>
        </div>
      </header>

      <section className="concierge-request-layout" aria-label="Private concierge enquiry">
        <form id="concierge-request-form" className="concierge-form" onSubmit={submit} aria-busy={saving}>
          <fieldset className="concierge-services" disabled={saving}>
            <legend><span>01</span> Your experience</legend>
            <div className="concierge-service-grid">
              {SERVICES.map((service) => (
                <label key={service} className="concierge-service">
                  <input type="checkbox" name="services" value={service}
                    checked={form.services.includes(service)} onChange={() => toggleService(service)} />
                  <span>{service}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="concierge-details" disabled={saving}>
            <legend><span>02</span> The details</legend>
            <div className="concierge-fields concierge-fields-three">
              <Field label="Destination / event">
                <input required value={form.destination} onChange={(e) => updateField("destination", e.target.value)}
                  placeholder="Destination or event name" />
              </Field>
              <Field label="Guests">
                <input type="number" min="1" step="1" required value={form.party_size}
                  onChange={(e) => updateField("party_size", e.target.value)} />
              </Field>
              <Field label="Travel dates">
                <input value={form.dates} onChange={(e) => updateField("dates", e.target.value)} placeholder="Dates or preferred month" />
              </Field>
            </div>
            <div className="concierge-fields">
              <Field label="Phone (optional)">
                <input type="tel" autoComplete="tel" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} />
              </Field>
              <Field label="Name">
                <input autoComplete="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} required />
              </Field>
              <Field label="Email">
                <input type="email" autoComplete="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} required />
              </Field>
              <Field label="Referral">
                <input value={referral} onChange={(e) => setReferral(e.target.value)}
                  placeholder="Affiliate name or code (optional)" />
              </Field>
            </div>
            <Field label="Anything else?">
              <textarea value={form.special_requests} onChange={(e) => updateField("special_requests", e.target.value)}
                placeholder="Occasion, favourite venues, preferred stay or special requests." rows="3" />
            </Field>
          </fieldset>

          <div className="concierge-submit-row">
            <p>A personal enquiry. No payment taken.</p>
            <button type="submit" className="concierge-submit" disabled={saving}>
              {saving ? "Sending enquiry..." : "Send enquiry"} <FiArrowRight aria-hidden="true" />
            </button>
          </div>
          {status && <p className="concierge-status" role="status">{status}</p>}
        </form>

        <aside className="concierge-aside">
          <img src="/assets/community/aaaa222222.png" alt="Private villa and pool overlooking the Mediterranean at sunset" />
          <p className="concierge-eyebrow">Consider it personal</p>
          <h2>The whole trip.<br />Every detail.</h2>
          <ul>
            <li><span>Stay</span>Hotels and private villas</li>
            <li><span>Experience</span>VIP tables, beach clubs and after parties</li>
            <li><span>Explore</span>Yacht days and private transfers</li>
          </ul>
          <a href="mailto:support@hey-lets-travel.com">Speak to the team <FiArrowUpRight aria-hidden="true" /></a>
        </aside>
      </section>

      <section className="concierge-overview">
        <p className="concierge-eyebrow">Beyond the booking</p>
        <h2>One trip. Made yours.</h2>
        <p>From event weekends to private celebrations, HLT sources hotels, villas, tables, yacht days and transfers around your plans. Ibiza, Marbella, London, Malta and Amsterdam.</p>
      </section>

      <section className="concierge-hotels" aria-labelledby="concierge-hotels-title">
        <div className="concierge-hotels-heading">
          <div>
            <p className="concierge-eyebrow">Ibiza / Selected stays</p>
            <h2 id="concierge-hotels-title">Prefer to book your stay directly?</h2>
          </div>
          <p>Explore our hotel partners, or leave the whole trip with us.</p>
        </div>
        <div className="concierge-hotel-grid">
          {HOTEL_PICKS.map((hotel) => (
            <a key={hotel.url} href={withAwinClickRef(hotel.url, affiliateCode)}
              className="concierge-hotel" {...externalLinkProps}>
              <div className="concierge-hotel-image">
                <img src={hotel.image} alt={hotel.name} loading="lazy" />
              </div>
              <div className="concierge-hotel-copy">
                <p className="concierge-hotel-meta">{hotel.meta}</p>
                <h3>{hotel.name}</h3>
                <p>{hotel.detail}</p>
                <span>View hotel</span>
              </div>
            </a>
          ))}
        </div>
        <p className="concierge-partner-note">Hotel bookings are made with lastminute.com. Partner links may earn HLT a commission.</p>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="concierge-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
